import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { BaseUser } from '../types/user';

interface AuthState {
  user: User | null;
  userData: BaseUser | null;
  loading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    userData: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      try {
        if (user) {
          // Fetch additional user data from Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            setState({
              user,
              userData: userDoc.data() as BaseUser,
              loading: false,
              error: null,
            });
          } else {
            setState({
              user,
              userData: null,
              loading: false,
              error: 'User data not found',
            });
          }
        } else {
          setState({
            user: null,
            userData: null,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        setState({
          user: null,
          userData: null,
          loading: false,
          error: error instanceof Error ? error.message : 'An error occurred',
        });
      }
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  const isAuthenticated = !!state.user;
  const isLoading = state.loading;
  const error = state.error;
  const user = state.user;
  const userData = state.userData;

  return {
    isAuthenticated,
    isLoading,
    error,
    user,
    userData,
  };
};

export default useAuth;
